import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import useIsMobileNav from '../../shared/hooks/useIsMobileNav';
import Icon from '../../shared/components/UIElements/Icon';
import './AdminSidebar.css';

const ADMIN_PAGES = [
  { label: 'Companies',   icon: 'companies', path: '/admin/companies' },
  { label: 'Users',       icon: 'users', path: '/admin/users' },
  { label: 'Projects',    icon: 'projects', path: '/admin/projects' },
  { label: 'Billing',     icon: 'billing', path: '/admin/billing' },
  { label: 'Simulations', icon: 'simulations',  path: '/admin/simulations' },
  { label: 'Support',     icon: 'support', path: '/admin/support' },
  { label: 'Badges',      icon: 'badges', path: '/admin/badges' },
];

const AdminSidebar = ({ embedded = false, onNavigate }) => {
  const isMobile = useIsMobileNav();
  const [isPinned, setIsPinned] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const sidebarVisible = isPinned || isHovered;

  // Notify App.js of pin state so it can shift main content. The embedded copy
  // occupies no column of its own, so it must never claim the 240px offset.
  useEffect(() => {
    if (embedded || isMobile) return undefined;
    window.dispatchEvent(
      new CustomEvent('adminSidebarPinChanged', { detail: { isPinned: true } })
    );
    return () => {
      window.dispatchEvent(
        new CustomEvent('adminSidebarPinChanged', { detail: { isPinned: false } })
      );
    };
  }, [embedded, isMobile]);

  // Navigating from the drawer dismisses it; the desktop sidebar stays put.
  const handleNavigate = () => {
    if (embedded) onNavigate?.();
  };

  const togglePin = () => {
    const next = !isPinned;
    setIsPinned(next);
    window.dispatchEvent(
      new CustomEvent('adminSidebarPinChanged', { detail: { isPinned: next } })
    );
  };

  let hoverTimeout = null;
  const handleMouseEnter = () => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    if (!isPinned) {
      hoverTimeout = setTimeout(() => setIsHovered(false), 300);
    }
  };

  // The desktop <aside> and the mobile drawer render the same link list.
  const navLinksBlock = (
    <nav className="admin-sidebar-nav">
      {ADMIN_PAGES.map((page) => (
        <NavLink
          key={page.path}
          to={page.path}
          className={({ isActive }) =>
            `admin-sidebar-link ${isActive ? 'active' : ''}`
          }
          onClick={handleNavigate}
        >
          <span className="admin-sidebar-icon"><Icon name={page.icon} size={18} /></span>
          <span className="admin-sidebar-label">{page.label}</span>
        </NavLink>
      ))}
    </nav>
  );

  // Mobile: the admin navigation is folded into the global side drawer so the
  // single ☰ in the header opens everything. Its old screen-edge pull tab sat
  // inside the browser's back-swipe gesture zone and could not be tapped.
  // Clicks stop here because SideDrawer dismisses itself on any click that
  // reaches it — only real navigation should close it.
  if (embedded) {
    return (
      <div className="drawer-admin-nav" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-nav-heading">Admin Console</div>
        {navLinksBlock}
      </div>
    );
  }

  if (isMobile) return null;

  return (
    <>
      {/* Hover trigger strip (this render path is desktop-only) */}
      <div className="admin-sidebar-trigger" onMouseEnter={handleMouseEnter} />

      <aside
        className={`admin-sidebar ${sidebarVisible ? 'visible' : ''} ${isPinned ? 'pinned' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Header */}
        <div className="admin-sidebar-header">
          <div className="admin-sidebar-header-top">
            <div className="admin-sidebar-title">Admin Console</div>
            <button
              className={`admin-sidebar-pin-btn ${isPinned ? 'pinned' : ''}`}
              onClick={togglePin}
              title={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
            >
              📌
            </button>
          </div>
        </div>

        {/* Navigation */}
        {navLinksBlock}

        <div className="admin-sidebar-footer">
          <span className="admin-sidebar-hint">
            {isPinned ? 'Sidebar pinned' : 'Hover to keep open'}
          </span>
        </div>
      </aside>

      {/* Backdrop — shown while the sidebar is open but unpinned */}
      {!isPinned && sidebarVisible && (
        <div
          className="admin-sidebar-backdrop"
          onClick={() => setIsHovered(false)}
        />
      )}
    </>
  );
};

export default React.memo(AdminSidebar);
